// Payment run — functional tabs for all payment methods.
//
// Mirrors `tricivo_system/operations/payment_run.php`: receipts are split
// across tabs by payment method. Bank Transfer keeps the BACS file-generation
// flow (Standard 18). Cheque / Bread4Scrap / Free Collect each get a
// select + "Mark as Paid" flow (records a ticket reference + paid date and
// flips payment_status). History shows every paid receipt with the method it
// was settled through.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckSquare, FileOutput, BadgeCheck } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Table, type Column } from '../../components/ui/Table/Table';
import { Badge } from '../../components/ui/Badge/Badge';
import { Modal } from '../../components/ui/Modal/Modal';
import { StatsStrip } from '../../components/ui/StatsStrip/StatsStrip';
import {
  useUnpaidReceipts,
  usePaidReceipts,
  useMarkReceiptsPaid,
} from '../bacs/hooks/useBacs';
import type { UnpaidReceiptDto } from '../../types/api';
import styles from './PaymentRunPage.module.css';

type Tab = 'bank' | 'cheque' | 'bread' | 'free' | 'history';

const TAB_LABELS: Record<Tab, string> = {
  bank: 'Bank Transfer',
  cheque: 'Cheque',
  bread: 'Bread4Scrap',
  free: 'Free Collect',
  history: 'History',
};

// Maps each tab to the payment_mode_name it filters on. History (null) shows
// every (paid) receipt regardless of method.
const TAB_MODE_MAP: Record<Tab, string | null> = {
  bank: 'Bank Transfer',
  cheque: 'Cheque',
  bread: 'Bread4Scrap',
  free: 'Free Collect',
  history: null,
};

// The three tabs settled via the Mark-as-Paid flow (everything except Bank
// Transfer, which uses the BACS run, and History, which is read-only).
type NonBacsMode = 'Cheque' | 'Bread4Scrap' | 'Free Collect';

function formatMoney(n: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function PaymentRunPage(): JSX.Element {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') ?? 'bank') as Tab;
  const isHistory = tab === 'history';
  const isBank = tab === 'bank';

  const unpaid = useUnpaidReceipts({ page: 1, limit: 200 });
  const paid = usePaidReceipts({ page: 1, limit: 200 });
  const markPaid = useMarkReceiptsPaid();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [ticketReference, setTicketReference] = useState('');
  const [paidDate, setPaidDate] = useState(todayIso());

  // Receipts for the active tab. History reads the paid list; every other tab
  // reads the unpaid list filtered by its payment method.
  const tabReceipts = useMemo<UnpaidReceiptDto[]>(() => {
    if (isHistory) return paid.data?.receipts ?? [];
    const all = unpaid.data?.receipts ?? [];
    const mode = TAB_MODE_MAP[tab];
    return all.filter((r) => r.payment_mode_name === mode);
  }, [isHistory, paid.data?.receipts, unpaid.data?.receipts, tab]);

  // Bank Transfer can only generate a BACS file for receipts that have full
  // banking details; the other tabs can mark any of their receipts paid.
  const eligible = useMemo(
    () => (isBank ? tabReceipts.filter((r) => r.has_banking_details) : tabReceipts),
    [isBank, tabReceipts],
  );

  // Reset selection whenever the tab changes — selection is per-tab.
  useEffect(() => {
    setSelected(new Set());
  }, [tab]);

  const totals = useMemo(() => {
    let value = 0;
    for (const r of tabReceipts) value += r.payable_amount ?? r.amount;
    const differ = tabReceipts.filter((r) => r.bank_details_changed).length;
    return { count: tabReceipts.length, value, eligible: eligible.length, differ };
  }, [tabReceipts, eligible.length]);

  function toggle(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  function toggleAll() {
    if (selected.size === eligible.length && eligible.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(eligible.map((r) => r.enc_id)));
    }
  }

  function setTab(next: Tab) {
    const url = new URLSearchParams(params);
    url.set('tab', next);
    setParams(url, { replace: false });
  }

  function generateForSelected() {
    if (selected.size === 0) return;
    const ids = Array.from(selected).join(',');
    navigate(`/collections/bacs/generate?receipts=${encodeURIComponent(ids)}`);
  }

  function generateForAll() {
    if (eligible.length === 0) return;
    const ids = eligible.map((r) => r.enc_id).join(',');
    navigate(`/collections/bacs/generate?receipts=${encodeURIComponent(ids)}`);
  }

  function openMarkPaid() {
    if (selected.size === 0) return;
    setTicketReference('');
    setPaidDate(todayIso());
    setModalOpen(true);
  }

  function confirmMarkPaid() {
    const mode = TAB_MODE_MAP[tab];
    if (mode === null || isBank) return; // guard — only non-BACS tabs reach here
    if (selected.size === 0 || ticketReference.trim().length === 0) return;
    markPaid.mutate(
      {
        receipt_enc_ids: Array.from(selected),
        payment_mode_name: mode as NonBacsMode,
        ticket_reference: ticketReference.trim(),
        paid_date: paidDate,
      },
      {
        onSuccess: () => {
          setSelected(new Set());
          setModalOpen(false);
        },
      },
    );
  }

  const checkboxColumn: Column<UnpaidReceiptDto> = {
    key: 'enc_id',
    header: '',
    width: '36px',
    render: (_v, row) =>
      isBank && !row.has_banking_details ? (
        <Badge variant="warning">Banking missing</Badge>
      ) : (
        <input
          type="checkbox"
          aria-label={`Select ${row.unique_id}`}
          checked={selected.has(row.enc_id)}
          onChange={() => toggle(row.enc_id)}
        />
      ),
  };

  const referenceColumn: Column<UnpaidReceiptDto> = {
    key: 'unique_id',
    header: 'Receipt #',
    render: (_v, row) => (
      <button
        type="button"
        className={styles.refLink}
        onClick={() => navigate(`/collections/receipts/${encodeURIComponent(row.enc_id)}`)}
      >
        {row.reference ?? row.unique_id}
      </button>
    ),
  };

  const commonColumns: Column<UnpaidReceiptDto>[] = [
    { key: 'receipt_date', header: 'Date', render: (_v, row) => formatDate(row.receipt_date) },
    { key: 'car_registration', header: 'Reg' },
    { key: 'customer_name', header: 'Customer' },
  ];

  const amountColumn: Column<UnpaidReceiptDto> = {
    key: 'amount',
    header: 'Amount',
    align: 'right',
    // Free Collect keeps its stored amount; what is actually payable is shown.
    render: (_v, row) =>
      row.payable_amount !== row.amount
        ? `${formatMoney(row.payable_amount)} (of ${formatMoney(row.amount)})`
        : formatMoney(row.amount),
  };

  // Non-blocking fraud control: bank details differ from the customer's stored ones.
  const bankCheckColumn: Column<UnpaidReceiptDto> = {
    key: 'bank_details_changed',
    header: 'Bank check',
    render: (_v, row) =>
      row.bank_details_changed ? <Badge variant="warning">Bank details changed</Badge> : null,
  };

  const paymentMethodColumn: Column<UnpaidReceiptDto> = {
    key: 'payment_mode_name',
    header: 'Payment Method',
    render: (_v, row) => row.payment_mode_name ?? '—',
  };

  const columns: Column<UnpaidReceiptDto>[] = isHistory
    ? [referenceColumn, ...commonColumns, paymentMethodColumn, amountColumn]
    : isBank
      ? [checkboxColumn, referenceColumn, ...commonColumns, bankCheckColumn, amountColumn]
      : [checkboxColumn, referenceColumn, ...commonColumns, amountColumn];

  const loading = isHistory ? paid.isLoading : unpaid.isLoading;

  const statsTiles = isHistory
    ? [
        { key: 'paid', label: 'Paid receipts', value: totals.count },
        { key: 'value', label: 'Total value', value: formatMoney(totals.value) },
      ]
    : [
        { key: 'count', label: 'Unpaid receipts', value: totals.count },
        ...(isBank
          ? [
              { key: 'banking', label: 'Banking ready', value: totals.eligible },
              { key: 'differ', label: 'Bank details changed', value: totals.differ },
            ]
          : []),
        { key: 'value', label: 'Total value', value: formatMoney(totals.value) },
        { key: 'selected', label: 'Selected', value: selected.size },
      ];

  return (
    <div className={styles.page}>
      <PageHeader
        title="Payment Run"
        subtitle={`${TAB_LABELS[tab]} · ${totals.count} receipt${
          totals.count === 1 ? '' : 's'
        } · ${formatMoney(totals.value)}`}
        actions={
          <Button variant="secondary" onClick={() => navigate('/collections/bacs/history')}>
            BACS history
          </Button>
        }
      />

      <StatsStrip ariaLabel="Payment run counters" tiles={statsTiles} />

      <div className={styles.tabs} role="tablist" aria-label="Payment run tabs">
        {(Object.keys(TAB_LABELS) as Tab[]).map((key) => (
          <button
            role="tab"
            type="button"
            aria-selected={tab === key}
            tabIndex={tab === key ? 0 : -1}
            key={key}
            className={tab === key ? styles.tabActive : styles.tab}
            onClick={() => setTab(key)}
          >
            {TAB_LABELS[key]}
          </button>
        ))}
      </div>

      <Card flush>
        {isBank ? (
          <div className={styles.bulkBar}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                aria-label="Select all on this page"
                checked={selected.size === eligible.length && eligible.length > 0}
                onChange={toggleAll}
              />
              <span>{selected.size > 0 ? `${selected.size} selected` : 'Select all on page'}</span>
            </label>
            <div className={styles.bulkActions}>
              <Button
                variant="secondary"
                size="sm"
                icon={CheckSquare}
                onClick={generateForSelected}
                disabled={selected.size === 0}
              >
                Generate BACS for Selected
              </Button>
              <Button
                variant="primary"
                size="sm"
                icon={FileOutput}
                onClick={generateForAll}
                disabled={eligible.length === 0}
              >
                Generate BACS for All
              </Button>
            </div>
          </div>
        ) : !isHistory ? (
          <div className={styles.bulkBar}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                aria-label="Select all on this page"
                checked={selected.size === eligible.length && eligible.length > 0}
                onChange={toggleAll}
              />
              <span>{selected.size > 0 ? `${selected.size} selected` : 'Select all on page'}</span>
            </label>
            <div className={styles.bulkActions}>
              <Button
                variant="primary"
                size="sm"
                icon={BadgeCheck}
                onClick={openMarkPaid}
                disabled={selected.size === 0}
              >
                Mark as Paid
              </Button>
            </div>
          </div>
        ) : null}

        <Table
          columns={columns}
          data={tabReceipts}
          keyField={(row) => row.enc_id}
          loading={loading}
          emptyState={
            <div className={styles.empty}>
              <h3>{isHistory ? 'No paid receipts' : `No ${TAB_LABELS[tab]} receipts`}</h3>
              <p>
                {isHistory
                  ? 'Receipts marked as paid will appear here.'
                  : `Approved ${TAB_LABELS[tab]} receipts awaiting payment will appear here.`}
              </p>
            </div>
          }
          ariaLabel={isHistory ? 'Paid receipts' : `${TAB_LABELS[tab]} receipts`}
        />
      </Card>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={`Mark as Paid — ${TAB_LABELS[tab]}`}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={confirmMarkPaid}
              disabled={ticketReference.trim().length === 0 || markPaid.isPending}
            >
              Confirm
            </Button>
          </>
        }
      >
        <div className={styles.modalForm}>
          <p className={styles.modalCount}>
            {selected.size} receipt{selected.size === 1 ? '' : 's'} selected.
          </p>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Ticket Reference</span>
            <input
              type="text"
              className={styles.fieldInput}
              value={ticketReference}
              onChange={(e) => setTicketReference(e.target.value)}
              placeholder="e.g. TKT-1042"
              aria-label="Ticket Reference"
              required
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Payment Date</span>
            <input
              type="date"
              className={styles.fieldInput}
              value={paidDate}
              onChange={(e) => setPaidDate(e.target.value)}
              aria-label="Payment Date"
              required
            />
          </label>
        </div>
      </Modal>
    </div>
  );
}
